import type { FileUpload, SubmissionData } from "@profile-service-types/index.ts";
import { format } from "date-fns";
import { cloneDeep } from "lodash";
import { IMAGE_EXTENSIONS } from "./components/DocumentView/utils.js";
import type { FormFileUpload, SubmissionFileOverride } from "./types";

type SubmissionDataOverride = (SubmissionData & Record<string, unknown>) | undefined;

export const formatDate = (date: string | Date | undefined): string => {
  if (!date) {
    return "";
  }
  try {
    return format(new Date(`${date}Z`), "MMM dd, yyyy · hh:mm a");
  } catch {
    return "";
  }
};

// Falls back across every spelling of the readable name before the stored object name.
export const getSubmissionFileName = (file: SubmissionFileOverride | undefined): string =>
  file?.original_file_name ?? file?.original_name ?? file?.file_name ?? "";

// Saved through a blob so the file keeps its own name rather than the object key.
export const downloadFileFromUrl = async ({ url, fileName }: { url: string; fileName: string }): Promise<void> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const isPdfFile = (fileName: string): boolean => fileName.toLowerCase().endsWith(".pdf");

export const isDocxFile = (fileName: string): boolean => fileName.toLowerCase().endsWith(".docx");

export const isHeicFile = (fileName: string): boolean => fileName.toLowerCase().endsWith(".heic");

export const isImageFile = (fileName: string): boolean =>
  IMAGE_EXTENSIONS.some((extension: string) => fileName.toLowerCase().endsWith(extension));

export const canPreviewFile = (fileName: string): boolean =>
  isPdfFile(fileName) || isImageFile(fileName) || isDocxFile(fileName) || isHeicFile(fileName);

const toFileUploadArray = (value: FormFileUpload): FileUpload[] | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return Array.isArray(value) ? value : [value];
};

// Derives the uploaded documents from a submission's raw submission_data.
export const getSubmissionDocuments = (submissionData: SubmissionDataOverride): FileUpload[] => {
  const passports = Array.isArray(submissionData?.passports) ? submissionData.passports : [];
  const passportUploads = passports
    .map((item: { image_upload?: FileUpload[] }) => item?.image_upload?.[0])
    .filter((upload): upload is FileUpload => Boolean(upload));

  const sources = [
    toFileUploadArray(submissionData?.document_upload as FormFileUpload),
    passportUploads,
    toFileUploadArray(submissionData?.resume_upload as FormFileUpload),
    toFileUploadArray(submissionData?.image_upload as FormFileUpload),
  ];

  return sources.find((source) => source?.length) ?? [];
};

// Strips fields we don't want to surface in the submission data JSON tree view.
export const getSubmissionJsonData = (submissionData: SubmissionDataOverride): Record<string, unknown> => {
  const data = cloneDeep(submissionData) as Record<string, unknown>;
  const passport = Array.isArray(data?.passports) ? data.passports : [];

  delete data?.application_id;
  delete data?.request_sent;
  delete data?.document_upload;
  delete data?.resume_upload;

  passport.forEach((item: { image_upload?: FileUpload[] }) => {
    delete item?.image_upload;
  });

  return data;
};
